import {
  Component,
  Input,
  EventEmitter,
  Output,
  OnInit,
  OnChanges,
  SimpleChanges,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { CollapsibleSectionComponent } from '../../../shared/collapsible-section/collapsible-section.component';
import { EmailPreviewComponent } from '../../email-preview/email-preview.component';
import { CreateEmailModalComponent } from './create-email-modal/create-email-modal.component';
import { NotificationService } from '../../../services/notification.service'; // NotificationService;
import {
  EmailData,
  DEFAULT_EMAIL_DATA,
  hasEmailContent,
} from '../../../services/email-preview.service';
import { findEmailContent } from '../optional-setting/sms-delivery.mapper';

interface EmailTemplate {
  id: string;
  subject: string;
  from: string;
  previewImage?: string;
}

/**
 * UI component responsible for managing, previewing, creating, editing, and clearing email campaign content within the campaign editor. It synchronizes sample email preview data with incoming campaign payloads, opens/controls create and edit modals, and emits changes back to parent components.
 * @component
 * @selector app-email
 * @example
 *   <app-email
 *     [isExpanded]="true"
 *     [campaignData]="campaign"
 *     [isCompleted]="false"
 *     (toggle)="handleToggle($event)"
 *     (done)="handleDone()"
 *     (dataChange)="handleDataChange($event)"
 *     (dataClear)="handleDataClear()">
 *   </app-email>
 * @input {boolean} isExpanded - Controls whether the email collapsible section is expanded (default: true).
 * @input {any} campaignData - Campaign payload (expected to contain contents.email) used to populate the sample email preview and form fields.
 * @input {boolean} isCompleted - Flag indicating whether the email step is marked completed in the parent workflow.
 * @output {EventEmitter<boolean>} toggle - Emitted when the email section is expanded/collapsed; payload is the new expanded state.
 * @output {EventEmitter<void>} done - Emitted when the user completes the email step (or triggers the legacy "Done" action).
 * @output {EventEmitter<any>} dataChange - Emitted with the current email form data whenever the form changes (used to persist/update campaign data).
 * @output {EventEmitter<void>} dataClear - Emitted when email templates/content are cleared (e.g., after delete), signaling the parent to clear stored email data.
 *
 * Key methods:
 * - hasEmailContent (getter): Determines whether there is meaningful email content to show, used to toggle between "Create" and "Done" behaviors.
 * - updateEmailDataFromCampaign(): Private helper that maps API campaignData.contents.email into the component's sampleEmailData (handles URLs vs File objects) and triggers change detection.
 * - resetEmailData(): Private helper that resets sampleEmailData to DEFAULT_EMAIL_DATA and marks the component for check.
 * - onEmailToggle(expanded: boolean): Emits the toggle event to the parent when user toggles the section.
 * - onEmailDone(): Contextual action: opens create modal if no content, otherwise emits done.
 * - onViewTemplate(template): Placeholder to view a template preview.
 * - onEditTemplate(template): Opens the edit modal and sets the editingTemplate.
 * - onDeleteTemplate(template): Deletes a template, clears email keywords/content, empties template list and emits a dataClear event.
 * - clearAllEmailKeywords(): Clears sample data, emits data change and triggers change detection.
 * - onCreateEmailCampaign(): Opens the create email modal.
 * - onCloseModal(), onCloseEditModal(): Close respective modals and reset modal state.
 * - onSaveEmail(data: EmailData): Saves a new email: updates preview immediately, closes modal, shows a success toast, and emits dataChange.
 * - onSaveEditEmail(data: EmailData): Saves edited email content similarly (update preview, close edit modal, success toast, emit dataChange).
 * - onFormChange(): Constructs a plain email payload from sampleEmailData, emits dataChange and marks for check to update dependent UI.
 *
 * Lifecycle hooks:
 * - ngOnInit(): Initializes sampleEmailData with default empty data to prepare the preview/form.
 * - ngOnChanges(): Responds to changes in @Input() campaignData and updates sampleEmailData via updateEmailDataFromCampaign or resets if appropriate.
 *
 * Implementation notes:
 * - Uses ChangeDetectorRef to force/mark change detection after asynchronous updates so the preview immediately reflects API or user changes.
 * - Uses a NotificationService to display toast notifications on create/update actions.
 * - Manages modal visibility (showCreateModal, showEditModal) and the currently editing template (editingTemplate).
 */
@Component({
  selector: 'app-email',
  imports: [
    CommonModule,
    CollapsibleSectionComponent,
    EmailPreviewComponent,
    CreateEmailModalComponent,
  ],
  templateUrl: './email.component.html',
  styleUrl: './email.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EmailComponent implements OnInit, OnChanges {
  @Input() isExpanded = true;
  @Input() campaignData: any = {};
  @Input() isCompleted = false;
  @Output() toggle = new EventEmitter<boolean>();
  @Output() done = new EventEmitter<void>();
  @Output() dataChange = new EventEmitter<any>();
  @Output() dataClear = new EventEmitter<void>();

  showCreateModal = false;
  showEditModal = false;
  editingTemplate: EmailTemplate | null = null;

  constructor(
    private cdr: ChangeDetectorRef,
    private messageService: NotificationService
  ) {}

  get hasEmailContent(): boolean {
    return hasEmailContent(this.sampleEmailData, this.campaignData);
  }

  ngOnInit(): void {
    // Initialize with empty data - will be populated when campaign data is available
    this.resetEmailData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['campaignData']) {
      this.updateEmailDataFromCampaign();
    }
  }

  /**
   * Update the component's sampleEmailData from campaign email content and trigger change detection.
   **/
  private updateEmailDataFromCampaign(): void {
    const emailContent = findEmailContent(this.campaignData?.contents);
    if (emailContent) {
      const logoUrl = emailContent.email_logo_img || '';
      const imageUrl = emailContent.email_body_img || '';

      this.sampleEmailData = {
        subject: emailContent.email_subject || '',
        fromEmail: emailContent.email_from_id || '',
        logo: null,
        logoUrl,
        image: null,
        imageUrl,
        header: emailContent.email_header || '',
        bodyText: emailContent.email_body_text || '',
        body2Text: emailContent.email_body_text2 || '',
        body3Text: emailContent.email_body_text3 || '',
        body4Text: emailContent.email_body_text4 || '',
        buttonText: emailContent.email_button_text || '',
        buttonPosition: (emailContent.email_button_position as 'top' | 'bottom') || 'bottom',
        buttonColor: emailContent.email_button_color || '#326FD1',
        carWashName: emailContent.email_footer_name || '',
        street: emailContent.email_footer_street || '',
        city: emailContent.email_footer_city || '',
        state: emailContent.email_footer_state || '',
        zipCode: emailContent.email_footer_zip || '',
        email: emailContent.email_reply_to || '',
        phoneNumber: emailContent.email_footer_phone || '',
      };
      this.cdr.detectChanges();
      return;
    }

    if (
      !this.sampleEmailData ||
      (!this.sampleEmailData.subject &&
        !this.sampleEmailData.header &&
        !this.sampleEmailData.bodyText &&
        !this.sampleEmailData.buttonText &&
        !this.sampleEmailData.carWashName)
    ) {
      this.resetEmailData();
    }
  }

  private resetEmailData(): void {
    this.sampleEmailData = { ...DEFAULT_EMAIL_DATA };
    this.cdr.markForCheck();
  }

  emailTemplates: EmailTemplate[] = [
    {
      id: '1',
      subject: '',
      from: '',
      previewImage: '',
    },
  ];

  // Sample email data for preview
  sampleEmailData: EmailData = { ...DEFAULT_EMAIL_DATA };

  onEmailToggle(expanded: boolean): void {
    this.toggle.emit(expanded);
  }

  onEmailDone(): void {
    // Check if button text is "Create" or "Done" based on hasEmailContent
    if (!this.hasEmailContent) {
      // Button shows "Create" - open the create email modal
      this.onCreateEmailCampaign();
    } else {
      // Button shows "Done" - emit the done event (keep old behavior)
      this.done.emit();
    }
  }

  onViewTemplate(template: EmailTemplate): void {
    // Implement view functionality
  }

  onEditTemplate(template: EmailTemplate): void {
    this.editingTemplate = template;
    this.showEditModal = true;
  }

  onDeleteTemplate(template: EmailTemplate): void {
    // Implement delete functionality

    // Clear all email keywords/tags from email content
    this.clearAllEmailKeywords();
    this.emailTemplates = [];
    this.dataClear.emit();
  }

  private clearAllEmailKeywords(): void {
    // Reset the sample email data to remove all content including keywords
    this.resetEmailData();

    // Emit data change event to update campaign data
    this.onFormChange();

    // Trigger change detection
    this.cdr.markForCheck();
  }

  onCreateEmailCampaign(): void {
    this.showCreateModal = true;
  }

  onCloseModal(): void {
    this.showCreateModal = false;
  }

  onCloseEditModal(): void {
    this.showEditModal = false;
    this.editingTemplate = null;
  }

  /**
   * Save a new email campaign locally, update the preview immediately, close the creation modal, show a success toast, and emit a change event to trigger persistence.
   * @example
   * nSaveEmail({ subject: 'Welcome', body: '<p>Hello</p>', recipients: [] })
   * undefined
   * @param {EmailData} data - Email campaign data to save and preview immediately.
   * @returns {void} No return value.
   */
  onSaveEmail(data: EmailData): void {
    // Update the sample email data with the new data immediately
    // This ensures preview shows right away, before API call completes
    this.sampleEmailData = { ...data };

    // Force change detection immediately to show preview right away
    this.cdr.detectChanges();

    // Close the modal
    this.showCreateModal = false;

    // Show success toast message for email creation
    this.messageService.add({
      severity: 'success',
      summary: 'Email campaign created!',
      detail: 'A new email campaign has been created successfully. ',
    });

    // Emit data change event to update campaign data (triggers API call after 3 sec debounce)
    this.onFormChange();
  }

  /**
   * Save edits to an email campaign, update the local preview immediately, close the edit modal, display a success message, and trigger a debounced API update.
   * @example
   * nSaveEditEmail({ subject: 'Spring Sale', body: '<p>Discounts!</p>' })
   * undefined
   * @param {EmailData} data - Updated email data for the campaign to be saved.
   * @returns {void} Does not return a value.
   **/
  onSaveEditEmail(data: EmailData): void {
    // Update the sample email data with the new data immediately
    // This ensures preview shows right away, before API call completes
    this.sampleEmailData = { ...data };

    // Force change detection immediately to show preview right away
    this.cdr.detectChanges();

    this.showEditModal = false;
    this.editingTemplate = null;
    this.messageService.add({
      severity: 'success',
      summary: 'Email campaign updated!',
      detail: 'The email campaign has been updated successfully. ',
    });
    // Emit data change event to update campaign data (triggers API call after 3 sec debounce)
    this.onFormChange();
  }

  /**
   * Assemble and emit the current sample email data object and trigger change detection.
   * @example
   * nFormChange()
   * undefined
   * @returns {void} Emits the assembled email data via the component's dataChange EventEmitter and calls markForCheck on the ChangeDetectorRef.
   **/
  onFormChange(): void {
    const emailData = {
      subject: this.sampleEmailData.subject,
      fromEmail: this.sampleEmailData.fromEmail,
      logo: this.sampleEmailData.logo,
      logoUrl: this.sampleEmailData.logoUrl,
      image: this.sampleEmailData.image,
      imageUrl: this.sampleEmailData.imageUrl,
      header: this.sampleEmailData.header,
      bodyText: this.sampleEmailData.bodyText,
      body2Text: this.sampleEmailData.body2Text,
      body3Text: this.sampleEmailData.body3Text,
      body4Text: this.sampleEmailData.body4Text,
      buttonText: this.sampleEmailData.buttonText,
      buttonPosition: this.sampleEmailData.buttonPosition,
      buttonColor: this.sampleEmailData.buttonColor,
      carWashName: this.sampleEmailData.carWashName,
      street: this.sampleEmailData.street,
      city: this.sampleEmailData.city,
      state: this.sampleEmailData.state,
      zipCode: this.sampleEmailData.zipCode,
      email: this.sampleEmailData.email,
      phoneNumber: this.sampleEmailData.phoneNumber,
    };

    this.dataChange.emit(emailData);

    // Trigger change detection to update hasEmailContent
    this.cdr.markForCheck();
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
